/**
 * Extracts a user-friendly error message from various error types, with special handling for ApiException
 * @param error - The error object caught from API calls
 * @returns A user-friendly error message string
 */
export async function extractErrorMessage(error: unknown): Promise<string> {
  let errorMessage = 'An unexpected error occurred';

  if (typeof error === 'object' && error) {
    // First, check if the error has a body.error structure (common API error format)
    // This takes priority over the 'message' property which may be a non-enumerable Error property
    if ('body' in error && typeof error.body === 'object' && error.body) {
      const body = error.body as Record<string, unknown>;
      if ('error' in body && typeof body.error === 'string') {
        return body.error;
      }
    }

    // Handle ApiException with code, body, headers structure
    if (
      'code' in error &&
      'message' in error &&
      typeof error.message === 'string'
    ) {
      // Try to get the actual response body from the cause parameter
      const apiException = error as {
        code: number;
        message: string;
        body: unknown;
        headers: Record<string, string>;
        cause?: unknown;
      };

      // Check if we have a cause with response body
      if (
        apiException.cause &&
        typeof apiException.cause === 'object' &&
        'body' in apiException.cause
      ) {
        const responseContext = apiException.cause as {
          body?: { text?: () => Promise<string> };
        };
        if (responseContext.body?.text) {
          try {
            const responseText = await responseContext.body.text();
            if (responseText) {
              // Try to parse as JSON to get the error message
              try {
                const responseData = JSON.parse(responseText);
                if (responseData.error) {
                  errorMessage = responseData.error;
                } else if (typeof responseData.detail === 'string') {
                  // FastAPI services (the lakehouse API) put their message in `detail`.
                  errorMessage = responseData.detail;
                } else {
                  errorMessage = responseText;
                }
              } catch {
                // If not JSON, use the raw text
                errorMessage = responseText;
              }
            }
          } catch {
            // If we can't get the response text, fall back to the message
          }
        }
      }

      // If we still have the default error message, try to extract from the ApiException message
      if (errorMessage === 'An unexpected error occurred') {
        const messageStr = apiException.message;
        if (messageStr.includes('Message:')) {
          // Extract the Message part from the ApiException message
          const messageMatch = /Message:\s*([^\n]+)/.exec(messageStr);
          if (messageMatch?.[1]) {
            errorMessage = messageMatch[1].trim();
          } else {
            errorMessage = messageStr;
          }
        } else {
          errorMessage = messageStr;
        }
      }
    } else if ('Message' in error && typeof error.Message === 'string') {
      errorMessage = error.Message;
    } else if ('response' in error) {
      const responseError = error as {
        response?: { data?: { error?: string } };
      };
      if (responseError.response?.data?.error) {
        errorMessage = responseError.response.data.error;
      }
    } else if ('message' in error && typeof error.message === 'string') {
      errorMessage = error.message;
    }
  } else if (typeof error === 'string') {
    // Try to parse the error string if it looks like a stringified object
    if (error.includes('HTTP-Code:') && error.includes('Message:')) {
      // Extract the Message part from the stringified error
      const messageMatch = /Message:\s*([^\n]+)/.exec(error);
      if (messageMatch?.[1]) {
        errorMessage = messageMatch[1].trim();
      } else {
        errorMessage = error;
      }
    } else {
      errorMessage = error;
    }
  }

  return errorMessage;
}
